import { PrimaryDialog, PrimaryDialogProps } from '@atoms/layout';
import { Alert, Box, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Button, LoadingButton } from '@atoms/buttons';
import { useHardDeleteModalStore } from '@libs/store/shifts';

type ShiftHardDeleteDialogProps = Omit<PrimaryDialogProps, 'open'> & {
  onCloseClick?: () => void;
  onSubmit?: (id: string) => Promise<void>;
  afterSubmit?: () => void;
};

export function ShiftHardDeleteDialog({ onCloseClick, onSubmit, afterSubmit, ...props }: ShiftHardDeleteDialogProps) {
  const { open, loading, id, externalId, hardDelete, closeModal } = useHardDeleteModalStore();

  const handleCLose = () => {
    onCloseClick && onCloseClick();
    closeModal();
  };

  const handleSubmit = async () => {
    onSubmit ? await onSubmit(id as string) : await hardDelete(id as string);
    afterSubmit && afterSubmit();
    handleCLose();
  };

  const localizationSubset = 'shifts.dialogs.hardDelete';

  return (
    <PrimaryDialog fullWidth maxWidth={'xs'} {...props} open={open}>
      <DialogTitle sx={{ px: 3, pt: 4, pb: 2 }}>
        <Typography variant="h5">
          <FormattedMessage id={`${localizationSubset}.title`} />
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Alert variant="outlined" severity="warning" sx={{ mb: 2 }}>
          <FormattedMessage id={`${localizationSubset}.baseBody`} values={{ externalId }} />
          <br />
          <FormattedMessage id={`${localizationSubset}.warnMessage`} />
        </Alert>
        <Typography variant="body2" color="textSecondary">
          <FormattedMessage id={`${localizationSubset}.subBody`} />
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Box color="text.secondary">
          <Button disabled={loading} variant="text" color="inherit" rounded onClick={handleCLose} size="large">
            <FormattedMessage id={`${localizationSubset}.cancelButton.label`} />
          </Button>
        </Box>
        <LoadingButton
          loading={loading}
          variant="contained"
          color="error"
          rounded
          onClick={handleSubmit}
          type="submit"
          size="large"
        >
          <FormattedMessage id={`${localizationSubset}.confirmButton.label`} />
        </LoadingButton>
      </DialogActions>
    </PrimaryDialog>
  );
}
